import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Search, Filter, ChevronDown, ChevronRight, Download,
  CheckCircle, AlertTriangle, Info, Zap, FileText, Building2,
  Cloud, Lock, Eye, Users, BarChart3, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import api from '@/services/api';

const DOMAIN_ICONS = {
  'AC': Lock,
  'IR': AlertTriangle,
  'RA': BarChart3,
  'VM': Users,
  'DP': Shield,
  'AL': FileText,
  'EN': Lock,
  'AI': Zap,
  'CS': Cloud,
  'BC': Layers,
  'SA': Users,
  'PM': FileText,
  'AM': Building2,
  'VS': Search,
  'PE': Building2
};

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-green-100 text-green-800 border-green-200'
};

const AUTOMATION_COLORS = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-700'
};

const USPCFControlsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [controls, setControls] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [highOverlap, setHighOverlap] = useState([]);
  const [cisoSummary, setCisoSummary] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  
  // Expanded control for detail view
  const [expandedControl, setExpandedControl] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Use v2 endpoints for updated USPCF data
      const [domainsRes, controlsRes, frameworksRes, overlapRes, summaryRes, roadmapRes] = await Promise.all([
        api.get('/uspcf/v2/domains'),
        api.get('/uspcf/v2/controls'),
        api.get('/uspcf/frameworks'),
        api.get('/uspcf/high-overlap'),
        api.get('/uspcf/ciso-summary'),
        api.get('/uspcf/implementation-roadmap')
      ]);
      
      // Transform v2 domain format
      const domainsData = domainsRes.data.domains?.map(d => ({
        id: d.name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        name: d.name,
        control_count: d.control_count
      })) || [];
      
      setDomains(domainsData);
      setControls(controlsRes.data.controls || []);
      setFrameworks(frameworksRes.data.frameworks || []);
      setHighOverlap(overlapRes.data.controls || []);
      setCisoSummary(summaryRes.data);
      setRoadmap(roadmapRes.data);
    } catch (err) {
      console.error('Failed to load USPCF data:', err);
      toast.error('Failed to load controls matrix');
    } finally {
      setLoading(false);
    }
  };

  // Filter controls
  const filteredControls = controls.filter(ctrl => {
    const matchesSearch = !searchTerm || 
      ctrl.control_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ctrl.control_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ctrl.full_text?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDomain = selectedDomain === 'all' || ctrl.domain === selectedDomain;
    
    const matchesFramework = selectedFramework === 'all' || 
      (ctrl.hipaa && selectedFramework === 'HIPAA') ||
      (ctrl.pci_dss && selectedFramework === 'PCI-DSS') ||
      (ctrl.gdpr && selectedFramework === 'GDPR') ||
      (ctrl.iso_27001 && selectedFramework === 'ISO-27001') ||
      (ctrl.soc2 && selectedFramework === 'SOC2');
    
    return matchesSearch && matchesDomain && matchesFramework;
  });

  // Group controls by domain
  const controlsByDomain = domains.map(domain => ({
    ...domain,
    id: domain.id || domain.name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
    controls: filteredControls.filter(c => c.domain === domain.name)
  })).filter(d => d.controls.length > 0);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="uspcf-controls-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-600" />
            USPCF Controls Matrix
          </h1>
          <p className="text-gray-500 mt-1">
            Unified Security & Privacy Controls Framework - {frameworks.length} Frameworks, {controls.length} Controls
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/grc/frameworks')}>
            <Layers className="w-4 h-4 mr-2" />
            My Frameworks
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Matrix
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Executive Overview</TabsTrigger>
          <TabsTrigger value="matrix">Controls Matrix</TabsTrigger>
          <TabsTrigger value="frameworks">Framework Mappings</TabsTrigger>
          <TabsTrigger value="roadmap">Implementation Roadmap</TabsTrigger>
        </TabsList>

        {/* Overview Tab - CISO Summary */}
        <TabsContent value="overview">
          {cisoSummary && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-indigo-500">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Frameworks Covered</p>
                    <p className="text-3xl font-bold">{cisoSummary.overview.frameworks_covered}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Unified Controls</p>
                    <p className="text-3xl font-bold">{cisoSummary.overview.unified_controls}</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">High-Overlap Controls</p>
                    <p className="text-3xl font-bold">{cisoSummary.overview.high_overlap_controls}</p>
                    <p className="text-xs text-gray-400">70%+ framework coverage</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Automation Coverage</p>
                    <p className="text-3xl font-bold">{cisoSummary.key_metrics.automation_coverage}</p>
                  </CardContent>
                </Card>
              </div>

              {/* High Overlap Controls */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Common Requirements (70%+ Coverage)
                  </CardTitle>
                  <CardDescription>
                    Implementing these 12 controls satisfies requirements across most frameworks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Control</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead>Frameworks</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Automation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {highOverlap.map(ctrl => (
                        <TableRow key={ctrl.control_id}>
                          <TableCell className="font-mono font-medium">{ctrl.control_id}</TableCell>
                          <TableCell>{ctrl.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              {ctrl.coverage}
                            </Badge>
                          </TableCell>
                          <TableCell>{ctrl.frameworks_count} frameworks</TableCell>
                          <TableCell>
                            <Badge className={PRIORITY_COLORS[ctrl.priority.toLowerCase()]}>
                              {ctrl.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={AUTOMATION_COLORS[ctrl.automation.toLowerCase()]}>
                              {ctrl.automation}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Strategic Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle>Strategic Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {cisoSummary.strategic_recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                          {i + 1}
                        </div>
                        <p className="text-gray-700">{rec}</p>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Framework Categories */}
              <Card>
                <CardHeader>
                  <CardTitle>Framework Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(cisoSummary.framework_categories).map(([category, fws]) => (
                      <div key={category} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium capitalize mb-2">
                          {category.replace('_', ' ')}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {fws.map(fw => (
                            <Badge key={fw} variant="outline" className="text-xs">
                              {fw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Controls Matrix Tab */}
        <TabsContent value="matrix">
          {/* Filters */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search controls..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedDomain} onValueChange={setSelectedDomain}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Domains" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Domains</SelectItem>
                    {domains.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedFramework} onValueChange={setSelectedFramework}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Frameworks" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frameworks</SelectItem>
                    {frameworks.slice(0, 15).map(f => (
                      <SelectItem key={f.code} value={f.code}>{f.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Priorities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Controls by Domain */}
          <Accordion type="multiple" className="space-y-2">
            {controlsByDomain.map(domain => {
              // Get icon based on domain name
              const domainKey = domain.name.toLowerCase().includes('access') ? 'AC' :
                               domain.name.toLowerCase().includes('governance') ? 'GOV' :
                               domain.name.toLowerCase().includes('incident') ? 'IR' :
                               domain.name.toLowerCase().includes('human') ? 'HR' :
                               domain.name.toLowerCase().includes('asset') ? 'AM' :
                               domain.name.toLowerCase().includes('crypto') ? 'EN' :
                               domain.name.toLowerCase().includes('operations') ? 'OP' :
                               domain.name.toLowerCase().includes('communication') ? 'COM' :
                               domain.name.toLowerCase().includes('system') ? 'SD' :
                               domain.name.toLowerCase().includes('business') ? 'BC' :
                               domain.name.toLowerCase().includes('compliance') ? 'COMP' : 'GOV';
              const DomainIcon = DOMAIN_ICONS[domainKey] || Shield;
              return (
                <AccordionItem key={domain.id} value={domain.id} className="border rounded-lg">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <DomainIcon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold">{domain.name}</p>
                        <p className="text-sm text-gray-500">{domain.controls.length} controls</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[140px]">Control ID</TableHead>
                          <TableHead>Control Name</TableHead>
                          <TableHead className="w-[120px]">Owner</TableHead>
                          <TableHead className="w-[200px]">Framework Mappings</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {domain.controls.map(ctrl => (
                          <TableRow 
                            key={ctrl.control_id}
                            className="cursor-pointer hover:bg-gray-50"
                            onClick={() => setExpandedControl(expandedControl === ctrl.control_id ? null : ctrl.control_id)}
                          >
                            <TableCell className="font-mono font-medium text-sm">{ctrl.control_id}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{ctrl.control_name}</p>
                                {expandedControl === ctrl.control_id && (
                                  <div className="mt-2 text-sm text-gray-600 space-y-2">
                                    <p>{ctrl.full_text}</p>
                                    {ctrl.evidence_requirements && (
                                      <p className="text-xs bg-blue-50 p-2 rounded">
                                        <strong>Evidence:</strong> {ctrl.evidence_requirements}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{ctrl.owner}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {ctrl.hipaa && <Badge variant="outline" className="text-xs bg-red-50">HIPAA</Badge>}
                                {ctrl.pci_dss && <Badge variant="outline" className="text-xs bg-green-50">PCI</Badge>}
                                {ctrl.gdpr && <Badge variant="outline" className="text-xs bg-purple-50">GDPR</Badge>}
                                {ctrl.iso_27001 && <Badge variant="outline" className="text-xs bg-blue-50">ISO</Badge>}
                                {ctrl.soc2 && <Badge variant="outline" className="text-xs bg-indigo-50">SOC2</Badge>}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </TabsContent>

        {/* Framework Mappings Tab */}
        <TabsContent value="frameworks">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {frameworks.map(fw => (
              <Card key={fw.code} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{fw.code}</CardTitle>
                    <Badge variant="outline">{fw.category}</Badge>
                  </div>
                  <CardDescription>{fw.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">{fw.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Region: {fw.region}</span>
                    <span className="text-gray-500">v{fw.version}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Implementation Roadmap Tab */}
        <TabsContent value="roadmap">
          {roadmap && (
            <div className="space-y-6">
              {Object.entries(roadmap.roadmap).map(([phase, data], index) => (
                <Card key={phase}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-green-500' : index === 1 ? 'bg-yellow-500' : 'bg-blue-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <CardTitle>{data.name}</CardTitle>
                        <CardDescription>{data.description}</CardDescription>
                      </div>
                      <Badge variant="outline" className="ml-auto">
                        Est. Coverage: {data.estimated_coverage}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {data.controls.map(ctrl => (
                        <Badge key={ctrl.control_id} variant="secondary">
                          {ctrl.control_id}: {ctrl.name}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default USPCFControlsPage;
